import * as React from 'react';
import { useContext, useEffect, useEffectEvent, useRef } from 'react';
import {
    addVegobjekttypeToSearch,
    replaceAdvancedFilterDraft,
} from '@/actions/vegobjekterActions';
import { useDispatch, useSelector } from 'react-redux';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import { stateConfig, stateSelectedRoadObjectTypes } from '@/selectors/selectors';
import { Dispatch } from '@/state/store';
import { DatakatalogMode } from './constants';
import { OverlayButtons } from './OverlayButtons';
import {
    appendAdvancedFilterNode,
    createAdvancedFilterEgenskapNode,
    createAdvancedFilterRelasjonNode,
} from '@/domain/advancedFilter';
import { OperatorE } from '@/middleware/queries';
import type {
    DatakatalogWindowHandle,
    DatakatalogWindowLauncher,
} from './standaloneChannel';
import { useDatakatalogTabMessages } from './useDatakatalogTabMessages';

interface DatakatalogContainerProps {
    open: boolean;
    shouldReset: boolean;
    setOpen: (open: boolean) => void;
    setShouldReset: (shouldReset: boolean) => void;
    mode?: DatakatalogMode;
    setMode?: (mode: DatakatalogMode) => void;
    tabToken: string | null;
    setTabToken: (token: string | null) => void;
    tabReady: boolean;
    setTabReady: (ready: boolean) => void;
    tabWindow: DatakatalogWindowHandle | null;
    setTabWindow: (window: DatakatalogWindowHandle | null) => void;
    standaloneTab: DatakatalogWindowLauncher;
}

const WINDOW_CLOSED_CHECK_INTERVAL = 250;

export const isValidPayload = (
    payload: unknown
): payload is {
    vegobjekttypeId: string | number;
    egenskapstypeId?: string | number;
    verdi?: string | number;
    relasjon?: string | number;
} => {
    if (payload == null || typeof payload !== 'object') return false;
    const { vegobjekttypeId } = payload as Record<string, unknown>;
    if (vegobjekttypeId == null) return false;
    const parsedId = Number(vegobjekttypeId);
    return !(!Number.isFinite(parsedId) || parsedId <= 0);
};

export const isTrustedDatakatalogMessage = (
    event: Pick<MessageEvent, 'origin' | 'source'>,
    {
        expectedOrigin,
        inlineSource,
    }: {
        expectedOrigin: string;
        inlineSource: MessageEventSource | null;
    }
) => event.origin === expectedOrigin && event.source === inlineSource;

const useMessageHandler = (
    iframeRef: React.RefObject<HTMLIFrameElement | null>,
    tabToken: string | null,
    open: boolean,
    tabReady: boolean,
    tabWindow: DatakatalogWindowHandle | null,
    setOpen: (open: boolean) => void,
    disconnectTab: () => void,
    onTabReady: () => void,
    onOpenInline: () => void,
    onCloseTab: () => void
) => {
    const { data } = useContext(ConfigAndDataContext);
    const config = useSelector(stateConfig);
    const dispatch: Dispatch = useDispatch();
    const vegobjekttyperInSearch = useSelector(stateSelectedRoadObjectTypes);
    const unloadTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (unloadTimeout.current) {
                clearTimeout(unloadTimeout.current);
                unloadTimeout.current = null;
            }
            if (pingTimeout.current) {
                clearTimeout(pingTimeout.current);
                pingTimeout.current = null;
            }
        },
        [tabToken]
    );

    const handleDatakatalogPayload = React.useCallback(
        (payload: unknown) => {
            if (!isValidPayload(payload)) {
                console.warn(
                    'Invalid payload received from datakatalog message',
                    payload
                );
                return;
            }

            const { vegobjekttypeId, egenskapstypeId, verdi, relasjon } = payload;
            const parsedVegobjekttypeId = Number(vegobjekttypeId);
            const parsedEgenskapstypeId =
                egenskapstypeId === undefined ? undefined : Number(egenskapstypeId);
            const parsedVerdi = verdi === undefined ? undefined : Number(verdi);
            const parsedRelasjon = relasjon === undefined ? undefined : Number(relasjon);
            if (
                [parsedEgenskapstypeId, parsedVerdi, parsedRelasjon].some(
                    id => id !== undefined && (!Number.isFinite(id) || id <= 0)
                )
            ) {
                console.warn(
                    'Invalid payload received from datakatalog message',
                    payload
                );
                return;
            }
            const vegobjektType = data.datakatalog.getTypeById(parsedVegobjekttypeId);

            if (!vegobjektType) {
                console.warn(
                    'Unknown vegobjekttypeId received from datakatalog:',
                    parsedVegobjekttypeId
                );
                return;
            }

            const existing = vegobjekttyperInSearch.find(
                vot => vot.typeId === vegobjektType.id
            );
            if (!existing) {
                dispatch(
                    addVegobjekttypeToSearch(
                        vegobjektType,
                        parsedEgenskapstypeId,
                        parsedVerdi,
                        parsedRelasjon
                    )
                );
            } else {
                let nextDraft = existing.getAdvancedFilterDraft();
                const egenskapstype = parsedEgenskapstypeId
                    ? vegobjektType.egenskapstypeById(parsedEgenskapstypeId)
                    : null;
                if (egenskapstype) {
                    nextDraft = appendAdvancedFilterNode(
                        nextDraft,
                        null,
                        createAdvancedFilterEgenskapNode(
                            egenskapstype,
                            parsedVerdi ? OperatorE.EQ : OperatorE.HAS_VALUE,
                            parsedVerdi
                                ? (egenskapstype.verdiById(parsedVerdi) ?? '')
                                : null
                        )
                    );
                }
                if (parsedRelasjon) {
                    nextDraft = appendAdvancedFilterNode(
                        nextDraft,
                        null,
                        createAdvancedFilterRelasjonNode(parsedRelasjon)
                    );
                }
                if (egenskapstype || parsedRelasjon) {
                    dispatch(replaceAdvancedFilterDraft(existing, nextDraft));
                }
            }
        },
        [data.datakatalog, dispatch, vegobjekttyperInSearch]
    );

    useEffect(() => {
        const expectedOrigin = new URL(config.datakatalogen_url).origin;
        const handleMessage = (event: MessageEvent) => {
            if (
                !isTrustedDatakatalogMessage(event, {
                    expectedOrigin,
                    inlineSource: iframeRef.current?.contentWindow ?? null,
                })
            ) {
                return;
            }
            if (event.data?.type !== 'ADD_VEGOBJEKT_FROM_DATAKATALOG') return;

            handleDatakatalogPayload(event.data.payload);
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [config.datakatalogen_url, iframeRef, handleDatakatalogPayload]);

    const sendTabMessage = useDatakatalogTabMessages(tabToken, message => {
        switch (message.type) {
            case 'READY':
                if (unloadTimeout.current) clearTimeout(unloadTimeout.current);
                unloadTimeout.current = null;
                if (pingTimeout.current) clearTimeout(pingTimeout.current);
                pingTimeout.current = null;
                onTabReady();
                if (!open) sendTabMessage({ type: 'CLOSE_TAB' });
                break;
            case 'ADD_VEGOBJEKT_FROM_DATAKATALOG':
                handleDatakatalogPayload(message.payload);
                break;
            case 'OPEN_INLINE':
                onOpenInline();
                break;
            case 'CLOSE':
                onCloseTab();
                break;
            case 'DISCONNECT':
                console.warn('Uventet frakoblingsmelding fra Datakatalog-fanen');
                break;
            case 'TAB_UNLOADED':
                if (unloadTimeout.current) clearTimeout(unloadTimeout.current);
                unloadTimeout.current = setTimeout(() => {
                    console.warn('Datakatalog-fanen ble lukket uten ny forbindelse');
                    disconnectTab();
                    setOpen(false);
                    unloadTimeout.current = null;
                }, 10000);
                break;
            case 'PING':
                console.warn('Uventet livstegn fra Datakatalog-fanen');
                break;
            case 'CLOSE_TAB':
                console.warn('Uventet lukkemelding fra Datakatalog-fanen');
                break;
        }
    });

    useEffect(() => {
        if (tabToken && !open) {
            sendTabMessage({ type: 'CLOSE_TAB' });
            tabWindow?.close();
        }
    }, [tabToken, open, sendTabMessage, tabWindow]);

    const handleTabWindowClosed = useEffectEvent(onCloseTab);

    useEffect(() => {
        if (!tabWindow) return;
        let closedTimeout: ReturnType<typeof setTimeout> | undefined;
        const interval = setInterval(() => {
            if (!tabWindow.isClosed()) return;
            clearInterval(interval);
            // Gi en eventuell OPEN_INLINE/CLOSE-melding fra vinduet tid til å komme fram først.
            closedTimeout = setTimeout(
                handleTabWindowClosed,
                WINDOW_CLOSED_CHECK_INTERVAL
            );
        }, WINDOW_CLOSED_CHECK_INTERVAL);
        return () => {
            clearInterval(interval);
            clearTimeout(closedTimeout);
        };
    }, [tabWindow]);

    useEffect(() => {
        // Vinduer med referanse overvåkes direkte; livstegn trengs bare for VS Code-faner.
        if (!tabToken || !tabReady || tabWindow) return;
        const interval = setInterval(() => {
            if (pingTimeout.current) return;
            sendTabMessage({ type: 'PING' });
            pingTimeout.current = setTimeout(() => {
                console.warn('Mistet forbindelsen til Datakatalog-fanen');
                disconnectTab();
                setOpen(false);
                pingTimeout.current = null;
            }, 7000);
        }, 3000);
        return () => clearInterval(interval);
    }, [tabToken, tabReady, tabWindow, sendTabMessage, disconnectTab, setOpen]);

    return handleDatakatalogPayload;
};

const useResizeObserver = (
    overlayRef: React.RefObject<HTMLDivElement | null>,
    mode: DatakatalogMode
) => {
    const [showReset, setShowReset] = React.useState(false);

    React.useEffect(() => {
        const overlay = overlayRef.current;
        if (!overlay || mode === DatakatalogMode.FLOATING_WINDOW) return;

        const checkManualResize = (element: HTMLElement) => {
            return ['width', 'height', 'left', 'right', 'top', 'bottom'].some(
                prop => element.style.getPropertyValue(prop) !== ''
            );
        };

        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                setShowReset(checkManualResize(entry.target as HTMLElement));
            }
        });

        ro.observe(overlay);
        setShowReset(checkManualResize(overlay));

        return () => ro.disconnect();
    }, [mode, overlayRef]);

    return showReset;
};

export const DatakatalogContainer = React.memo(
    ({
        open,
        shouldReset,
        setOpen,
        setShouldReset,
        mode = DatakatalogMode.BIG,
        setMode,
        tabToken,
        setTabToken,
        tabReady,
        setTabReady,
        tabWindow,
        setTabWindow,
        standaloneTab,
    }: DatakatalogContainerProps) => {
        const config = useSelector(stateConfig);
        const overlayRef = useRef<HTMLDivElement>(null);
        const iframeRef = useRef<HTMLIFrameElement>(null);
        const displayMode =
            mode === DatakatalogMode.FLOATING_WINDOW ? DatakatalogMode.BIG : mode;
        const overlayVisible = open && !tabReady && !tabWindow;

        const handleClose = () => {
            setShouldReset(true);
            setOpen(false);
        };

        const disconnectTab = React.useCallback(() => {
            setTabToken(null);
            setTabReady(false);
            setTabWindow(null);
        }, [setTabToken, setTabReady, setTabWindow]);

        useMessageHandler(
            iframeRef,
            tabToken,
            open,
            tabReady,
            tabWindow,
            setOpen,
            disconnectTab,
            () => {
                setTabReady(true);
            },
            () => {
                disconnectTab();
                setMode?.(DatakatalogMode.BIG);
                setOpen(true);
            },
            () => {
                disconnectTab();
                handleClose();
            }
        );
        const showReset = useResizeObserver(overlayRef, displayMode);

        useEffect(() => {
            if (shouldReset && iframeRef.current) {
                iframeRef.current.src = config.datakatalogen_url;
                setShouldReset(false);
            }
        }, [shouldReset, setShouldReset, config.datakatalogen_url]);

        return (
            <div
                ref={overlayRef}
                className={`datakatalog-overlay${overlayVisible ? ' datakatalog-overlay--visible' : ''}${
                    displayMode === DatakatalogMode.BIG ? ' datakatalog-overlay--big' : ''
                }`}
                style={{ display: overlayVisible ? 'block' : 'none' }}
            >
                <OverlayButtons
                    mode={displayMode}
                    setMode={setMode}
                    setShouldReset={setShouldReset}
                    onClose={handleClose}
                    showReset={showReset}
                    overlayRef={overlayRef}
                    standaloneTab={{ ...standaloneTab, pending: tabToken !== null }}
                />
                <iframe
                    ref={iframeRef}
                    src={config.datakatalogen_url}
                    title="Datakatalogen"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    className={`datakatalog-iframe${open ? ' datakatalog-iframe--visible' : ''}`}
                />
            </div>
        );
    }
);

DatakatalogContainer.displayName = 'DatakatalogContainer';
